import React, { useCallback, useEffect, useMemo, useState } from "react";
import style from "./stylesheets/HomePage.module.scss";
import Hint from "./Hint";
import HintList from "./HintList";
import { getFunctions, httpsCallable } from "firebase/functions";
import moment from "moment";
import { fetchUserData } from "../helpers/data";
import Onboarding from "./Onboarding";
import { getUID } from "../helpers/localStorage";
import Logo from "./Logo";
import SlashHeader from "./SlashHeader";
import Leaderboard from "./Leaderboard";
import Footer from "./Footer";

const HomePage = () => {
    const functions = getFunctions();
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [hints, setHints] = useState([]);
    const [error, setError] = useState("");
    const [currentTime, setCurrentTime] = useState(Date.now());
    const uid = getUID();

    const unreleasedHints = useMemo(() =>
        hints.filter(hint => hint.releaseTime > currentTime),
        [hints, currentTime]
    );
    const releasedHints = useMemo(() =>
        hints.filter(hint => hint.releaseTime <= currentTime),
        [hints, currentTime]
    );
    const nextUpHint = useMemo(() => {
        const notFoundHints = releasedHints
            .filter(i => !userData || !userData.foundHints.includes(i.id))
            .filter(i => !i.isEventStamp || i.eventEndTime >= Date.now());

        if(notFoundHints.length === 0) return null;
        return notFoundHints.reduce((a, b) => {
            // Prioritize event stamps
            if(a.isEventStamp && !b.isEventStamp) return a;
            if(!a.isEventStamp && b.isEventStamp) return b;
            
            // Prioritize events that are ending sooner, or creation time to break ties
            if(a.isEventStamp && b.isEventStamp) {
                if(a.eventEndTime === b.eventEndTime) return a.createdAt < b.createdAt ? a : b;
                return a.eventEndTime < b.eventEndTime ? a : b;
            }

            // Otherwise, prioritize by release time, then creation time
            if(a.releaseTime === b.releaseTime) return a.createdAt < b.createdAt ? a : b;
            return a.releaseTime > b.releaseTime ? a : b;
        });
    }, [releasedHints, userData]);
    const nextUnreleasedHint = useMemo(() =>
        unreleasedHints.length === 0 ? null :
        unreleasedHints.reduce((a, b) => a.releaseTime < b.releaseTime ? a : b),
        [unreleasedHints]
    );

    const fetchHints = useCallback(async () => {
        let result;
        try {
            const func = httpsCallable(functions, "getAllHints");
            result = await func();
        } catch(e) {
            console.error(e);
            setError("Unexpected error")
            return;
        }
        if(!result.data || !result.data.hints) {
            setError("Failed to fetch hints");
            console.log(result);
            return;
        }
        setLoading(false);
        setHints(result.data.hints);
    }, [functions]);

    useEffect(() => {
        const interval = setInterval(() => {
            if(nextUnreleasedHint && Date.now() > nextUnreleasedHint.releaseTime) {
                fetchHints();
            }
            setCurrentTime(Date.now());
        }, 1000);
        return () => clearInterval(interval);
    }, [fetchHints, nextUnreleasedHint]);

    useEffect(() => {
        fetchHints();
    }, [fetchHints, uid]);

    useEffect(() => {
        if(!uid) return;

        (async () => {
            try {
                const data = await fetchUserData(functions);
                setUserData(data);
            } catch(e) {
                console.error(e);
                setError("Failed to fetch user data");
                return;
            }
        })();
    }, [functions, uid]);

    const nextHintBox = useMemo(() => {
        if(!nextUnreleasedHint) return null;
        console.log(nextUnreleasedHint)
        const diff = nextUnreleasedHint.releaseTime - currentTime;
        const duration = moment.duration(diff, "milliseconds");
        const days = Math.ceil(duration.asDays());
        return (
            <div id={style.new_drop}>
                <SlashHeader>Coming Soon</SlashHeader>
                {
                    days > 1 ? (
                        <p>
                            Next hint drops in {days} days
                        </p>
                    ) :
                    (
                        <p>
                            Next hint drops in&nbsp;
                            {String(duration.hours()).padStart(2, "0")}:
                            {String(duration.minutes()).padStart(2, "0")}:
                            {String(duration.seconds()).padStart(2, "0")}
                        </p>
                    )
                }
            </div>
        );
    }, [currentTime, nextUnreleasedHint]);

    if(loading) {
        return (
            <div id={style.home_page} className={style.loading}>
                {error && <p id={style.error}>{error}</p>}
                <h2>Loading...</h2>
            </div>
        );
    }

    
    if(!uid) {
        return <Onboarding />;
    }

    const foundHints = userData?.foundHints || [];
    const numPoints = foundHints
        .map(hintId => hints.find(h => h.id === hintId))
        .reduce((sum, hint) => sum + (hint ? hint.reward : 0), 0);


    return (
        <div id={style.home_page}>
            <Logo />
            <p id={style.blurb}>
                Get excited about all things engineering for <strong>Engineering Week 2026</strong>!
                Attend events and find scavenger hunt items to earn points and win prizes.
                Each point is an entry into the swag raffle! And, the top three participants will win grand prizes.
            </p>
            {error && <p id={style.error}>{error}</p>}
            <SlashHeader>Next Up</SlashHeader>
            <Hint hintData={nextUpHint} />
            <SlashHeader>Things To Do</SlashHeader>
            <HintList hintData={releasedHints} userData={userData} showEventStamps={true} />
            <SlashHeader>Scavenger Hunt</SlashHeader>
            <HintList hintData={releasedHints} userData={userData} showEventStamps={false} />
            <SlashHeader>Leaderboard</SlashHeader>
            <Leaderboard userData={userData} />
            {nextHintBox}
            <Footer />
        </div>
    )
};

export default HomePage;
